<template>
  <div class="q-gutter-md">
    <customer-loader :customer-id="customerId" v-model="customer" />
    <show-finally v-if="customer && isTransactional" :task="task" loader>
      <choose-payment-method
        v-if="!isFree"
        :customer-id="customerId"
        :payment-method-id="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
      />
    </show-finally>
    <show-finally v-else-if="customer && !isTransactional" :task="task" loader>
      <message>
        This charge will be added to your next monthly invoice.
      </message>
    </show-finally>

    <q-list v-if="price" bordered class="radius">
      <q-item>
        <q-item-section>Subtotal</q-item-section>
        <q-item-section class="text-subtitle2" avatar>
          {{ $filters.currency(price.subTotal) }} {{ currency }}
        </q-item-section>
      </q-item>
      <q-separator />
      <div v-if="showPromos">
        <q-item-label header class="q-pb-none">Promotions</q-item-label>
        <q-item v-for="promo in price.promos" :key="promo.name">
          <q-item-section>
            {{ promo.name }}
            <div><small>{{ promo.description }}</small>
            </div>
          </q-item-section>
          <q-item-section class="text-subtitle2 text-negative" avatar>
            -{{ $filters.currency(promo.amount) }} {{ currency }}
          </q-item-section>
        </q-item>
        <q-separator />
      </div>
      <div v-if="showTaxes">
        <q-item-label header class="q-pb-none">Taxes</q-item-label>
        <q-item v-for="tax in price.taxes" :key="tax.abbrev">
          <q-item-section>{{ tax.abbrev }}</q-item-section>
          <q-item-section class="text-subtitle2" avatar>
            {{ $filters.currency(tax.amount) }} {{ currency }}
          </q-item-section>
        </q-item>
        <q-separator />
      </div>
      <q-item>
        <q-item-section>Total</q-item-section>
        <q-item-section class="text-subtitle2" avatar>
          {{ $filters.currency(price.total) }} {{ currency }}
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import ShowFinally from '@/components/task/ShowFinally.vue';
import { Customer } from '@/billing/types';
import CustomerLoader from '@/billing/customer/CustomerLoader.vue';
import Message from '@/components/message/Message.vue';
import ChoosePaymentMethod from './ChoosePaymentMethod.vue';
import { Price, calculatePrice } from './pricing';

interface Data {
  customer: Customer|null,
  price: Price|null,
  task: Promise<any>|null,
}

export default defineComponent({
  emits: ['done', 'update:modelValue'],
  components: {
    Message,
    CustomerLoader,
    ShowFinally,
    ChoosePaymentMethod,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
    modelValue: Number,
  },
  data(): Data {
    return {
      customer: null,
      price: null,
      task: null,
    };
  },
  computed: {
    isTransactional(): boolean {
      return (
        this.customer !== null
        && this.customer.billingMethod === 'transactional'
      );
    },
    isFree(): boolean {
      if (!this.price) return false;
      return (this.price.total === 0);
    },
    showPromos(): boolean {
      if (!this.price) return false;
      return (this.price.promos.length > 0);
    },
    showTaxes(): boolean {
      return !this.isFree;
    },
    isDone(): boolean {
      return (
        this.isFree
        || !this.isTransactional
        || (!this.isFree && !_.isNil(this.modelValue))
      );
    },
    currency(): string|null {
      return this.price ? this.price.currency.toUpperCase() : null;
    },
  },
  watch: {
    customerId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    isDone: {
      handler(isDone: boolean): void {
        this.$emit('done', isDone);
      },
      immediate: true,
    },
  },
  methods: {
    load() {
      this.task = calculatePrice(this.customerId);
      this.task.then((price: Price) => {
        this.price = price;
      });
    },
  },
  filters: {
    currency(amount: number): string {
      return (amount / 100).toFixed(2);
    },
  },
});
</script>
